import { SmartChargingStatus } from '@vae/server/src/interfaces/eNode/eNodeSmartCharging.interface'
import { ENodeVehicle } from '@vae/server/src/interfaces/eNode/eNodeVehicle.interface'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useUser from '../../../../contexts/hooks/useUser'
import SmartChargingConsidering from '../smartCharging/SmartChargingConsidering'
import SmartChargingGraph from '../smartCharging/SmartChargingGraph'
import { SmartChargeController } from './SmartChargeController'
import SmartChargingInfo from './SmartChargingInfo'

interface Props {
	smartChargingStatus: SmartChargingStatus | undefined | null
	vehicle?: ENodeVehicle | null
}

const SmartChargingVisualiser: React.FC<Props> = ({ smartChargingStatus, vehicle }) => {
	const { user } = useUser()

	const { t } = useTranslation('pages_electricCar', { keyPrefix: 'smartChargingVisualiser' })

	const chargingPlan = useMemo(() => {
		const plan = smartChargingStatus?.plan
		if (!plan) return null

		return {
			...plan,
			state: smartChargingStatus.state,
			consideration: smartChargingStatus.consideration,
			startAt: dayjs(plan?.startAt),
			stopAt: dayjs(plan?.stopAt),
			estimatedFinishAt: dayjs(plan?.estimatedFinishAt),
			startTimeString: dayjs(plan?.startAt).format('HH:mm'),
			stopTimeString: dayjs(plan?.estimatedFinishAt).format('HH:mm'),
			estimatedChargeTime: dayjs(plan?.estimatedFinishAt).diff(dayjs(plan?.startAt), 'minute'),
			chargeTime: {
				hours: Math.floor(dayjs(plan?.estimatedFinishAt).diff(dayjs(plan?.startAt), 'minute') / 60),
				minutes: dayjs(plan?.estimatedFinishAt).diff(dayjs(plan?.startAt), 'minute') % 60, // Calculate remaining minutes after calculating hours
			},
		}
	}, [smartChargingStatus])

	const hasSmartPlan = !!chargingPlan

	if (!vehicle || !user) return null

	return (
		<div>
			<div className='top-texts'>
				{vehicle.smartChargingPolicy.isEnabled && (
					<div className='header flex justify-center'>
						<div className='visualizer tracking-wide text-text-dark dark:text-white'>
							{t('lataa')}
							<SmartChargeController vehicles={[vehicle]} />
							{t('mennessa')}
						</div>
					</div>
				)}

				{chargingPlan && <SmartChargingInfo chargingPlan={chargingPlan} />}

				<SmartChargingConsidering
					vehicle={vehicle}
					consideration={chargingPlan?.consideration}
					hasSmartPlan={hasSmartPlan}
				/>

				{smartChargingStatus?.plan && chargingPlan && vehicle.smartChargingPolicy.isEnabled && (
					<>
						<div className='additional dark:text-brand-primary-100-dark'>
							<div className='additional-text'>
								{t('jatkuu')}
								<b>{chargingPlan.startTimeString}</b>
								{t('paattyy')}
								<b>{chargingPlan.stopTimeString}</b>
							</div>
						</div>
						<SmartChargingGraph
							chargingPlan={smartChargingStatus?.plan}
							targetTime={parseInt(vehicle?.smartChargingPolicy.deadline ?? '0')}
						/>
					</>
				)}
			</div>
		</div>
	)
}
export default SmartChargingVisualiser
